import React, { useMemo } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { HeaderSecondary, InfoBlock, LastUpdateBlock, Wrapper } from './SecondaryHeaderStyled';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import { SegmentsData } from '@/lib/types/hypertrackerTypes';
import { nFormatter } from '@/lib/helpers/helpers';
import ThemedTooltip from '@/components/Common/ThemedTooltip';
import { parseStringDurationShort } from '@/lib/helpers/durationHelpers';
import { fetchVolumeData } from '@/api/hlApi';
import { usePolling } from '@/hooks/usePolling';
import useStatusUpdateTimer from '@/hooks/hypertracker/useStatusUpdateTimer';

const updateSequence = 360;

const StatusUpdateDisplay = React.memo(function StatusUpdateDisplay() {
    const { t } = useTranslation('translation', { keyPrefix: 'secondary_header' });
    const theme = useTheme();
    const { past, future } = useStatusUpdateTimer();

    return (
        <LastUpdateBlock>
            <Divider orientation="vertical" sx={{ margin: 0, width: '1px', height: '12px' }} />
            <ThemedTooltip
                background={theme.palette.backgrounds.htBgGradient}
                sx={{
                    maxWidth: '250px',
                }}
                followCursor
                placement={'bottom-start'}
                title={t('last_update_tooltip')}
            >
                <Typography
                    sx={{ alignSelf: { xs: 'flex-end', sm: 'unset' }, cursor: 'help' }}
                    variant="p12"
                    color="text.secondary"
                    noWrap
                >
                    {t('last_update_label', { duration: past || '-----------' })}
                </Typography>
            </ThemedTooltip>

            <Divider orientation="vertical" sx={{ margin: 0, width: '1px', height: '12px' }} />
            <ThemedTooltip
                background={theme.palette.backgrounds.htBgGradient}
                sx={{
                    maxWidth: '250px',
                }}
                followCursor
                placement={'bottom-start'}
                title={t('next_update_tooltip')}
            >
                <Typography
                    sx={{ alignSelf: { xs: 'flex-end', sm: 'unset' }, cursor: 'help' }}
                    variant="p12"
                    color="text.secondary"
                    noWrap
                >
                    {t('next_update_label', {
                        duration: future || '-----------',
                    })}
                </Typography>
            </ThemedTooltip>
            <Box data-testid="header-circle-time" sx={{ display: 'flex' }}>
                <CircularProgress
                    variant="determinate"
                    sx={{ color: theme.palette.backgrounds.bg7 }}
                    size={12}
                    thickness={22}
                    value={100}
                />
                <Box sx={{ transform: 'rotateY(180deg)' }}>
                    <CircularProgress
                        variant="determinate"
                        sx={{
                            color: theme.palette.hyperLiquidColor2,
                            position: 'absolute',
                        }}
                        size={12}
                        thickness={22}
                        value={
                            ((updateSequence - parseStringDurationShort((future || '') as string)) /
                                updateSequence) *
                            100
                        }
                    />
                </Box>
            </Box>
        </LastUpdateBlock>
    );
});

interface SecondaryHeaderProps {
    segmentsData: SegmentsData | null;
}

const SecondaryHeader: React.FC<SecondaryHeaderProps> = ({ segmentsData }) => {
    const { t } = useTranslation('translation', { keyPrefix: 'secondary_header' });
    const theme = useTheme();
    const [updatedVolumeData, setUpdatedVolumeData] = React.useState<number | null>(null);

    const { openInterestChange, openInterest, perpVolume, perpVolume24HoursAgo } = useMemo(() => {
        if (segmentsData) {
            const deltaIO =
                segmentsData?.perpPositionsMetrics?.openInterest -
                segmentsData?.perpPositionsMetrics24HoursAgo?.openInterest;

            return {
                openInterestChange: deltaIO,
                openInterest: segmentsData?.perpPositionsMetrics?.openInterest,
                perpVolume: segmentsData?.perpVolume,
                perpVolume24HoursAgo: segmentsData?.perpVolume24HoursAgo,
            };
        }

        return {
            openInterestChange: undefined,
            openInterest: undefined,
            perpVolume: undefined,
            perpVolume24HoursAgo: undefined,
        };
    }, [segmentsData]);

    usePolling(
        async () => {
            await fetchActualVolumeData();
        },
        5 * 60 * 1000,
        false,
    );

    const fetchActualVolumeData = async () => {
        const { data } = await fetchVolumeData({
            type: 'metaAndAssetCtxs',
        });

        const volume = Object.values(data[1]).reduce(
            (acc: number, item) => acc + (item ? +item['dayNtlVlm'] : 0),
            0,
        );
        setUpdatedVolumeData(volume);
    };

    const getVolumeData = () => {
        return updatedVolumeData || perpVolume || 0;
    };

    const getVolumeDataDifference = () => {
        return getVolumeData() - (perpVolume24HoursAgo || 0);
    };

    const getTextColor = (value: number) => {
        if (value > 0) return 'success.main';
        if (value < 0) return 'error.main';
        return 'customText.text4';
    };

    return (
        <Wrapper>
            <HeaderSecondary>
                <InfoBlock>
                    <ThemedTooltip
                        background={theme.palette.backgrounds.htBgGradient}
                        sx={{
                            maxWidth: '250px',
                        }}
                        followCursor
                        placement={'bottom-start'}
                        title={
                            <Typography variant={'p10'} color={'customText.text4'}>
                                <Trans
                                    t={t}
                                    i18nKey="open_interest_tooltip"
                                    values={{
                                        direction:
                                            openInterestChange && openInterestChange < 0
                                                ? t('down_label')
                                                : t('up_label'),
                                        formattedOpenInterest: nFormatter(openInterest, true),
                                        formattedOpenInterestChange:
                                            openInterestChange && openInterestChange > 0
                                                ? `+${nFormatter(openInterestChange, true)}`
                                                : nFormatter(openInterestChange, true),
                                    }}
                                    defaults={`Perp Open Interest: %{formattedOpenInterest}<br/>%{direction} %{formattedOpenInterestChange} in the last 1 hour`}
                                />
                            </Typography>
                        }
                    >
                        <Typography
                            data-testid="header-oi"
                            sx={{ cursor: 'help' }}
                            variant={'p12'}
                            fontWeight={400}
                            whiteSpace={'nowrap'}
                        >
                            <Trans
                                t={t}
                                values={{
                                    formattedOpenInterest: nFormatter(openInterest, true),
                                    formattedOpenInterestChange:
                                        openInterestChange && openInterestChange > 0
                                            ? `+${nFormatter(openInterestChange, true)}`
                                            : nFormatter(openInterestChange, true),
                                }}
                                i18nKey="open_interest"
                                defaults={`<gray>OI:</gray> %{formattedOpenInterest} <sensitive>(%{formattedOpenInterestChange})</sensitive>`}
                                components={{
                                    gray: (
                                        <Typography
                                            key={'key_1'}
                                            variant={'p12'}
                                            fontWeight={400}
                                            color={'customText.text1'}
                                            component={'span'}
                                        />
                                    ),
                                    sensitive: (
                                        <Typography
                                            key={'key_2'}
                                            variant={'p12'}
                                            fontWeight={400}
                                            color={getTextColor(openInterestChange || 0)}
                                            component={'span'}
                                            sx={{
                                                display:
                                                    openInterestChange && +openInterestChange === 0
                                                        ? 'none'
                                                        : 'inline',
                                            }}
                                        />
                                    ),
                                }}
                            />
                        </Typography>
                    </ThemedTooltip>

                    <Divider
                        orientation="vertical"
                        sx={{ margin: 0, width: '1px', height: '12px' }}
                    />
                    <ThemedTooltip
                        background={theme.palette.backgrounds.htBgGradient}
                        sx={{
                            maxWidth: '250px',
                        }}
                        followCursor
                        placement={'bottom-start'}
                        title={
                            <Typography variant={'p10'} color={'customText.text4'}>
                                {t('24h_vol_tooltip')}
                            </Typography>
                        }
                    >
                        <Typography
                            data-testid="header-volume"
                            sx={{ cursor: 'help' }}
                            variant={'p12'}
                            fontWeight={400}
                            whiteSpace={'nowrap'}
                        >
                            <Trans
                                t={t}
                                values={{
                                    perpVolume: nFormatter(getVolumeData(), true),
                                    perpVolumeChange:
                                        getVolumeDataDifference() > 0
                                            ? `+${nFormatter(getVolumeDataDifference(), true)}`
                                            : `${nFormatter(getVolumeDataDifference(), true)}`,
                                }}
                                i18nKey="24h_vol"
                                defaults={`<gray>24h Vol:</gray> %{perpVolume} <sensitive>(%{perpVolumeChange})</sensitive>`}
                                components={{
                                    gray: (
                                        <Typography
                                            key={'key_1'}
                                            variant={'p12'}
                                            fontWeight={400}
                                            color={'customText.text1'}
                                            component={'span'}
                                        />
                                    ),
                                    sensitive: (
                                        <Typography
                                            key={'key_2'}
                                            variant={'p12'}
                                            fontWeight={400}
                                            color={getTextColor(getVolumeDataDifference())}
                                            component={'span'}
                                            sx={{
                                                display:
                                                    getVolumeDataDifference() === 0
                                                        ? 'none'
                                                        : 'inline',
                                            }}
                                        />
                                    ),
                                }}
                            />
                        </Typography>
                    </ThemedTooltip>
                </InfoBlock>
                <StatusUpdateDisplay />
            </HeaderSecondary>
        </Wrapper>
    );
};

export default SecondaryHeader;
